import PaymentsConstants from "./PaymentsConstants";
import call from "../../service/http";

export default {
  namespaced: true,
  state: {
    pendingRequests: [],
    history: [],
    serviceTypes: [],
    requestDetails: null,
    loading: false,
    alert: {
      status: "",
      message: "",
    },
    pagination: {
      pending: {
        page: 1,
        itemsPerPage: 10,
        total: 0,
        currentCursor: null,
        nextCursor: null,
      },
      history: {
        page: 1,
        itemsPerPage: 10,
        total: 0,
        currentCursor: null,
        nextCursor: null,
      },
    },
  },
  mutations: {
    SET_PENDING_REQUESTS: (state, payload) => {
      state.pendingRequests = payload;
    },
    APPEND_PENDING_REQUESTS: (state, payload) => {
      state.pendingRequests = [...state.pendingRequests, ...payload];
    },
    SET_HISTORY: (state, payload) => {
      state.history = payload;
    },
    APPEND_HISTORY: (state, payload) => {
      state.history = [...state.history, ...payload];
    },
    SET_SERVICE_TYPES: (state, payload) => {
      state.serviceTypes = payload;
    },
    SET_REQUEST_DETAILS: (state, payload) => {
      state.requestDetails = payload;
    },
    SET_LOADING: (state, payload) => {
      state.loading = payload;
    },
    SET_ALERT: (state, payload) => {
      state.alert = payload || {
        status: "",
        message: "",
      };
    },
    SET_PAGINATION: (state, { type, data }) => {
      state.pagination[type] = { ...state.pagination[type], ...data };
    },
  },
  getters: {
    pendingRequests: (state) => state.pendingRequests,
    history: (state) => state.history,
    serviceTypes: (state) => state.serviceTypes,
    requestDetails: (state) => state.requestDetails,
    loading: (state) => state.loading,
    alert: (state) => state.alert,
    pagination: (state) => state.pagination,
  },
  actions: {
    getPendingRequests: ({ commit }, params = {}) => {
      commit("SET_ALERT", null);
      commit("SET_LOADING", true);

      call("get", PaymentsConstants.pendingRequests(params))
        .then((res) => {
          const payload = res.data?.data;
          if (!payload) {
            commit("SET_PENDING_REQUESTS", []);
            commit("SET_LOADING", false);
            return;
          }
          const data = payload.data || payload;
          const total = payload.total || 0;
          const nextCursor = payload.nextCursor ?? null;
          const currentCursor = payload.currentCursor ?? null;

          commit("SET_PENDING_REQUESTS", Array.isArray(data) ? data : []);
          commit("SET_PAGINATION", {
            type: "pending",
            data: {
              total: total,
              currentCursor: nextCursor,
              nextCursor: currentCursor,
            },
          });
          commit("SET_LOADING", false);
        })
        .catch((err) => {
          commit("SET_LOADING", false);
          commit("SET_ALERT", {
            status: "error",
            message:
              err.response?.data?.message || "Failed to fetch pending requests",
          });
        });
    },

    loadMorePendingRequests: ({ commit }, params = {}) => {
      commit("SET_LOADING", true);

      return call("get", PaymentsConstants.pendingRequests(params))
        .then((res) => {
          const payload = res.data?.data;
          if (!payload) {
            commit("SET_LOADING", false);
            return;
          }
          const data = payload.data || payload;
          const total = payload.total || 0;
          const nextCursor = payload.nextCursor ?? null;
          const currentCursor = payload.currentCursor ?? null;

          commit("APPEND_PENDING_REQUESTS", Array.isArray(data) ? data : []);
          commit("SET_PAGINATION", {
            type: "pending",
            data: {
              total: total,
              currentCursor: nextCursor,
              nextCursor: currentCursor,
            },
          });
          commit("SET_LOADING", false);
        })
        .catch((err) => {
          commit("SET_LOADING", false);
          commit("SET_ALERT", {
            status: "error",
            message:
              err.response?.data?.message ||
              "Failed to load more pending requests",
          });
        });
    },

    getHistory: ({ commit }, params = {}) => {
      commit("SET_ALERT", null);
      commit("SET_LOADING", true);

      call("get", PaymentsConstants.history(params))
        .then((res) => {
          const payload = res.data?.data;
          if (!payload) {
            commit("SET_HISTORY", []);
            commit("SET_LOADING", false);
            return;
          }
          const data = payload.data || payload;
          const total = payload.total || 0;
          const nextCursor = payload.nextCursor ?? null;
          const currentCursor = payload.currentCursor ?? null;

          commit("SET_HISTORY", Array.isArray(data) ? data : []);
          commit("SET_PAGINATION", {
            type: "history",
            data: {
              total: total,
              currentCursor: nextCursor,
              nextCursor: currentCursor,
            },
          });
          commit("SET_LOADING", false);
        })
        .catch((err) => {
          commit("SET_LOADING", false);
          commit("SET_ALERT", {
            status: "error",
            message:
              err.response?.data?.message || "Failed to fetch payment history",
          });
        });
    },

    loadMoreHistory: ({ commit }, params = {}) => {
      commit("SET_LOADING", true);

      return call("get", PaymentsConstants.history(params))
        .then((res) => {
          const payload = res.data?.data;
          if (!payload) {
            commit("SET_LOADING", false);
            return;
          }
          const data = payload.data || payload;
          const total = payload.total || 0;
          const nextCursor = payload.nextCursor ?? null;
          const currentCursor = payload.currentCursor ?? null;

          commit("APPEND_HISTORY", Array.isArray(data) ? data : []);
          commit("SET_PAGINATION", {
            type: "history",
            data: {
              total: total,
              currentCursor: nextCursor,
              nextCursor: currentCursor,
            },
          });
          commit("SET_LOADING", false);
        })
        .catch((err) => {
          commit("SET_LOADING", false);
          commit("SET_ALERT", {
            status: "error",
            message:
              err.response?.data?.message || "Failed to load more history",
          });
        });
    },

    getServiceTypes: ({ commit }, params) => {
      call("get", PaymentsConstants.serviceTypes(params))
        .then((res) => {
          commit("SET_SERVICE_TYPES", res.data.data);
        })
        .catch((err) => {
          console.error("Failed to fetch service types", err);
        });
    },

    createRequest: ({ commit, dispatch }, payload) => {
      commit("SET_LOADING", true);
      commit("SET_ALERT", null);

      return call("post", PaymentsConstants.createRequest, payload)
        .then((res) => {
          commit("SET_LOADING", false);
          commit("SET_ALERT", {
            status: "success",
            message: "Service request created successfully",
          });
          dispatch("getPendingRequests");
          return res.data.data;
        })
        .catch((err) => {
          commit("SET_LOADING", false);
          commit("SET_ALERT", {
            status: "error",
            message: err.response?.data?.message || "Failed to create request",
          });
          return false;
        });
    },

    getRequestDetails: ({ commit }, id) => {
      commit("SET_LOADING", true);
      call("get", PaymentsConstants.requestDetails(id))
        .then((res) => {
          commit("SET_REQUEST_DETAILS", res.data.data);
          commit("SET_LOADING", false);
        })
        .catch((err) => {
          commit("SET_LOADING", false);
          commit("SET_ALERT", {
            status: "error",
            message: err.response?.data?.message || "Failed to fetch details",
          });
        });
    },
  },
};
